import {
	AddLineItemButtonWrapper,
	AddLineItemContainer,
	AddLineItemInputFormControl,
	AddLineItemStandardContainer,
	AddLineItemStandardDimensions
} from "./AddLineItemStyles.ts";
import { Button, TextField } from "@mui/material";
import {
	ChangeEvent,
	FormEventHandler,
	ReactNode,
	RefObject,
	SyntheticEvent,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import AutoComplete from "../../Common/Autocomplete/Autocomplete.tsx";
import { lineItemIdSymbol, newOrderApiSlice, useGetProductsQuery } from "features/api/newOrderApi.ts";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { addColorMatchSchema, schema, SkuAndDescription } from "./schema.ts";
import {
	AddLineAddAnotherItemButton,
	AddLineAddAnotherTextOne,
	AddLineCancelButton,
	AddLineDepthLabel,
	AddLineEnterText,
	AddLineHeightLabel,
	AddLineItemHeader,
	AddLineItemInvalidProductError,
	AddLineItemProductPlaceholder,
	AddLineItemQuantityPlaceholder,
	AddLineItemRequired,
	AddLineItemStandardDimensionsHeader,
	AddLineSaveButton,
	AddLineWidthLabel,
	ProductSelectFormError,
	RequiredDimensionError,
	SelectRequiredSpecifications
} from "./constants.ts";
import { models } from "types/api/viewModels.ts";
import { calculateIntervals, isDimensionValid } from "components/NewOrders/utils/NewOrderUtils.tsx";
import {
	DimensionContainer,
	RequiredCategoryWrapper,
	RequiredDimensionsValues
} from "../RequiredDimensions/RequiredDimensionsStyles.ts";
import AddModification from "../AddModification/AddModification.tsx";
import { getFormFieldProps } from "utils/form.tsx";
import useAddModification from "components/NewOrders/AddModification/hooks/useAddModification.ts";
import { matchSorter } from "match-sorter";
import { v4 as uuidv4 } from "uuid";
import AddColorMatch from "../AddColorMatch/AddColorMatch.tsx";
import { useGetPaintVendorsQuery } from "features/api/globalsApi.ts";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import { useDispatch, useSelector } from "react-redux";
import { AddAnotherColorText, CustomText } from "constants/text.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { RootState } from "stores/application.store.tsx";
import { convertSkuAndDescriptionToString } from "utils/string.ts";

interface AddNewLineItemModalProps {
	configuration?: models["PendingOrderConfigurationViewModel"];
	handleLineItem: (
		configurationId: string,
		newLineItem: models["PendingLineItemViewModel"],
		globals: models["GlobalsViewModel"]
	) => void;
	selectedEditItem?: RefObject<models["PendingLineItemViewModel"] | models["CqModLineItemViewModel"] | undefined>;
	isAddLineItem?: boolean;
	isCustomQuote?: boolean;
	productLineId?: string | null;
	className?: string;
	children?: ReactNode;
	defaultItem?: models["PendingLineItemViewModel"] | models["CqModLineItemViewModel"] | null;
	onLoadingChange?: (isLoading: boolean) => void;
	addItemVisibility?(groupIndex?: number, configIndex?: number): void;
}

type LineItemFilterOptions = (items: SkuAndDescription[], state: { inputValue: string }) => SkuAndDescription[];
const filterOptions: LineItemFilterOptions = (items, { inputValue }) =>
	matchSorter(items, inputValue, { keys: [(item) => convertSkuAndDescriptionToString(item.sku, item.description)] });

const AddLineItem = ({
	addItemVisibility,
	configuration,
	handleLineItem,
	selectedEditItem,
	isAddLineItem = false,
	isCustomQuote = false,
	productLineId,
	className,
	children,
	defaultItem,
	onLoadingChange
	// eslint-disable-next-line sonarjs/cognitive-complexity
}: AddNewLineItemModalProps) => {
	const defaultPendingItem = defaultItem as models["PendingLineItemViewModel"] | null | undefined;
	const defaultSku = defaultItem?.sku ?? null;
	const defaultDescription = defaultPendingItem?.description ?? defaultSku ?? undefined;
	const defaultSearchValue = defaultSku ? convertSkuAndDescriptionToString(defaultSku, defaultDescription) : null;

	const [quantityValue, setQuantityValue] = useState(defaultPendingItem?.quantityOrdered ?? 1);
	const [searchValue, setSearchValue] = useState<string | null>(
		defaultSku ? convertSkuAndDescriptionToString(defaultSku, defaultDescription) : null
	);
	const dimensions = useRef<Record<string, number | null>>({});
	const [submitted, setSubmitted] = useState(new Date().getTime());
	const originalSubmitted = useRef(submitted);
	const [requiredDimensionsValues, setRequiredDimensionsValues] = useState<
		(models["PendingValueViewModel"] | null)[]
	>(defaultPendingItem?.requiredDimensions ?? []);
	const { isCustomColorPage: isCustomColor, isAddOnPage } = useUserLocation();
	const dispatch = useDispatch();
	const param = configuration?.globals?.productLine?.id ?? productLineId ?? "";

	const getProductsParams = useMemo(
		() => ({
			productLineId: param,
			sampleProducts: isCustomColor
		}),
		[param, isCustomColor]
	);

	const selectResult = useSelector((state: RootState) =>
		newOrderApiSlice.endpoints.getProducts.select(getProductsParams)(state)
	);

	const { data: apiData, isLoading: apiIsLoading } = useGetProductsQuery(getProductsParams, { skip: !param });

	const newLineItems = selectResult.data ?? apiData;
	const isLoading = selectResult.isLoading || apiIsLoading;

	const { data: paintVendors, isLoading: isVendorsLoading } = useGetPaintVendorsQuery(
		configuration?.globals?.productLine?.id ?? "",
		{ skip: !configuration || isCustomQuote }
	);

	const foundSearch = useMemo(() => {
		const skuFromSearchValue = searchValue?.split(" - ")[0];
		return newLineItems?.find((item) => item.sku === skuFromSearchValue);
	}, [newLineItems, searchValue]);
	const isDefaultSelection = Boolean(defaultSearchValue) && searchValue === defaultSearchValue;
	const isProductListLoading = isLoading && !isDefaultSelection;
	const isSelectionResolving = Boolean(searchValue) && !foundSearch && isLoading;
	const requiredDimensionsCount =
		foundSearch?.requiredDimensions?.length ??
		(isDefaultSelection ? defaultPendingItem?.requiredDimensions?.length : 0) ??
		0;
	const isRequiredDimensionsInitializing =
		requiredDimensionsCount > 0 && requiredDimensionsValues.length !== requiredDimensionsCount;
	const isLineItemLoading = isProductListLoading || isSelectionResolving || isRequiredDimensionsInitializing;

	const { control, handleSubmit, trigger, formState, register, getValues, reset } = useForm({
		mode: "onChange",
		resolver: yupResolver(schema),
		reValidateMode: "onChange",
		defaultValues: {
			productSelect: defaultSku ? { sku: defaultSku, description: defaultDescription } : undefined,
			productSelectQuantity: defaultPendingItem?.quantityOrdered ?? undefined
		}
	});
	const formFieldData = { formState, register, schema };
	const { productSelect } = getValues();
	const defaultItemRef = useRef<models["PendingLineItemViewModel"] | models["CqModLineItemViewModel"] | undefined>(
		defaultItem ?? undefined
	);

	useEffect(() => {
		if (defaultItem && !defaultItemRef.current) {
			defaultItemRef.current = defaultItem;
		}
	}, [defaultItem]);

	const {
		control: requiredCategoriesControl,
		getValues: requiredCategoriesGetValues,
		handleSubmit: requiredCategoriesHandleSubmit,
		trigger: triggerRequiredCategories,
		reset: resetRequiredCategoriesForm
	} = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues:
			defaultPendingItem?.requiredCategories?.reduce(
				(acc, category) => {
					if (category.categoryName) {
						acc[category.categoryName] = category;
					}
					return acc;
				},
				{} as Record<string, any>
			) ?? {}
	});

	const {
		control: colorMatchControl,
		trigger: colorMatchTrigger,
		setValue: colorMatchSetValue,
		getValues: colorMatchGetValues,
		clearErrors: colorMatchClearErrors
	} = useForm({
		resolver: yupResolver(addColorMatchSchema),
		mode: "onSubmit"
	});

	const {
		getModificationFormErrors,
		handleSetModificationFormData,
		handleSetModificationFormErrors,
		modificationFormErrors,
		modificationFormValues,
		modifications
	} = useAddModification(
		foundSearch?.id ?? selectedEditItem?.current?.id,
		configuration?.globals?.productLine?.id ?? productLineId
	);
	useEffect(() => {
		setQuantityValue(defaultPendingItem?.quantityOrdered ?? 1);
		setSearchValue(defaultSku ? convertSkuAndDescriptionToString(defaultSku, defaultDescription) : null);
	}, [defaultDescription, defaultPendingItem?.quantityOrdered, defaultSku]);

	useEffect(() => {
		onLoadingChange?.(isLineItemLoading);
	}, [isLineItemLoading, onLoadingChange]);

	useEffect(() => {
		if (defaultSku) {
			reset({
				productSelect: { sku: defaultSku, description: defaultDescription },
				productSelectQuantity: defaultPendingItem?.quantityOrdered
			});
		} else {
			reset({
				productSelect: undefined,
				productSelectQuantity: undefined
			});
		}
	}, [defaultDescription, defaultPendingItem?.quantityOrdered, defaultSku, reset]);

	useEffect(() => {
		// When defaultItem exists, use its required categories
		// Otherwise, use an empty object (for when a new product is selected)
		const defaultValues =
			defaultPendingItem?.requiredCategories?.reduce(
				(acc, category) => {
					if (category.categoryName) {
						acc[category.categoryName] = category;
					}
					return acc;
				},
				{} as Record<string, any>
			) ?? {};
		resetRequiredCategoriesForm(defaultValues);
	}, [defaultPendingItem?.requiredCategories, resetRequiredCategoriesForm]);

	// Reset required categories when the product search changes (foundSearch changes)
	useEffect(() => {
		// If there's a foundSearch product and it's different from the defaultItem, reset the form
		if (foundSearch) {
			const isDefaultProduct = defaultItem?.sku === foundSearch.sku;
			if (!isDefaultProduct) {
				resetRequiredCategoriesForm({});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [foundSearch?.sku, defaultItem?.sku]);

	useEffect(() => {
		if (foundSearch?.requiredDimensions?.length) {
			const initialValues =
				foundSearch.requiredDimensions.map((dimension) => {
					const defaultDimension = defaultPendingItem?.requiredDimensions?.find(
						(defaultDim) => defaultDim.id === dimension.id
					);
					return defaultDimension ?? null;
				}) ?? [];
			setRequiredDimensionsValues(initialValues);
			return;
		}

		if (isDefaultSelection && defaultPendingItem?.requiredDimensions?.length) {
			setRequiredDimensionsValues(defaultPendingItem.requiredDimensions);
			return;
		}

		setRequiredDimensionsValues([]);
	}, [defaultPendingItem?.requiredDimensions, foundSearch?.requiredDimensions, isDefaultSelection]);

	useEffect(() => {
		if (defaultPendingItem?.requiredDimensions) {
			dimensions.current = {};
			defaultPendingItem.requiredDimensions.forEach((dimension) => {
				if (dimension.id && dimension.value !== undefined && dimension.value !== null) {
					dimensions.current[dimension.id] = dimension.value;
				}
			});
		} else {
			dimensions.current = {};
		}
	}, [defaultPendingItem?.requiredDimensions]);

	const inputRef = useRef<HTMLInputElement | undefined>(undefined);

	useEffect(() => {
		const handleQuickKeys = (event: any) => {
			if (event.key === "\\") {
				event.preventDefault();
				handleAddAnother();
			}
		};
		document.addEventListener("keydown", handleQuickKeys);

		return () => {
			document.removeEventListener("keydown", handleQuickKeys);
		};
	});

	const handleChangeProduct = (_event: SyntheticEvent, values: SkuAndDescription | null) => {
		setSearchValue(convertSkuAndDescriptionToString(values?.sku, values?.description) ?? null);
		handleSetModificationFormData(new Map());
		handleSetModificationFormErrors(new Map());
		setRequiredDimensionsValues([]);
		setSubmitted(originalSubmitted.current);
		resetRequiredCategoriesForm({});
	};

	const quantityValues = Array.from({ length: 99 }, (_, i) => i + 1);
	const quantity = quantityValues.join().split(",");

	const newLineItemMap: SkuAndDescription[] | undefined = useMemo(() => {
		if (!isLoading) {
			return newLineItems?.map((item) => {
				return { sku: item.sku, description: item.description };
			});
		}
	}, [isLoading, newLineItems]);

	const sortSkuAndDescription = (items: SkuAndDescription[] | undefined): SkuAndDescription[] | undefined => {
		return items?.sort((a, b) => {
			const aKey = a.sku?.trim().toLowerCase() ?? a.description?.trim().toLowerCase() ?? "";
			const bKey = b.sku?.trim().toLowerCase() ?? b.description?.trim().toLowerCase() ?? "";
			return aKey.localeCompare(bKey);
		});
	};

	const sortedOptions = useMemo(() => sortSkuAndDescription(newLineItemMap), [newLineItemMap]);

	const handleMouseDownCapture = (e: SyntheticEvent) => {
		e.stopPropagation();
	};

	const handleQuantityChange = (_event: SyntheticEvent, value: number) => {
		setQuantityValue(value);
	};

	const handleDimensionChange = (
		id: string | null | undefined,
		value: number | string,
		description: string | null | undefined,
		increment: number | undefined,
		maxValue: number | undefined,
		minValue: number | undefined,
		isUnrestrictedValue: boolean | undefined,
		index: number
	) => {
		const parsedValue = typeof value === "string" ? parseFloat(value) : value;

		const requiredDimensions: models["RequiredDimensionsViewModel"] & models["PendingValueViewModel"] = {
			id: id,
			description: description,
			value: parsedValue,
			increment: increment,
			maxValue: maxValue,
			minValue: minValue,
			isUnrestrictedValue: isUnrestrictedValue
		};

		setRequiredDimensionsValues((prev) =>
			prev.map((dimension, prevIndex) => {
				if (index === prevIndex) {
					return requiredDimensions;
				}
				return dimension;
			})
		);
	};

	const handleSaveAction = async (
		addAnother = false,
		requiredCategoryValues?: { [key: string]: models["RequiredCategoryValueViewModel"] }
	) => {
		setSubmitted(new Date().getTime());
		const { errors, isValid } = getModificationFormErrors(modificationFormValues);

		handleSetModificationFormErrors(errors);

		const modifications: models["PendingModificationViewModel"][] = Array.from(modificationFormValues.values()).map(
			(mod) => {
				const { id, sku, description } = mod ?? {};
				const modificationData: models["PendingModificationViewModel"] = {
					id,
					sku,
					description,
					lineItemNumber: undefined,
					itemKey: sku,
					...(mod && "values" in mod && Array.isArray(mod.values)
						? { values: (mod as { values: models["PendingValueViewModel"][] }).values }
						: {})
				};
				return modificationData;
			}
		);

		const { productSelect } = getValues();
		const resolvedLineItem = (foundSearch ?? (isDefaultSelection ? defaultPendingItem : undefined)) as
			| models["PendingLineItemViewModel"]
			| undefined;
		const requiredDimensionsLength = resolvedLineItem?.requiredDimensions?.length ?? 0;

		if (
			resolvedLineItem &&
			searchValue !== null &&
			requiredDimensionsValues.length === requiredDimensionsLength &&
			requiredDimensionsValues.every((dimension) => dimension !== null) &&
			isValid
		) {
			const requiredValues = Object.values(requiredCategoryValues ?? {});
			const newLineItem: models["PendingLineItemViewModel"] = {
				...resolvedLineItem,
				[lineItemIdSymbol]: uuidv4(),
				description: productSelect?.description ?? resolvedLineItem.description,
				lineItemNumber: 1,
				sku: productSelect?.sku ?? resolvedLineItem.sku,
				modifications: modifications,
				quantityOrdered: quantityValue,
				requiredDimensions: requiredDimensionsValues.filter((dimension) => dimension !== null),
				requiredCategories: requiredValues
			};

			handleLineItem(configuration?.configurationId ?? "", newLineItem, configuration?.globals ?? {});

			if (!isCustomQuote) {
				setSearchValue(null);
				setQuantityValue(1);
				reset();
			}

			if (!addAnother) {
				addItemVisibility?.();
			}
		}
	};

	const handleAddAnother = async () => {
		Promise.all([trigger(), triggerRequiredCategories()]).then(async (validity) => {
			const { errors, isValid } = getModificationFormErrors(modificationFormValues);
			const { paintVendor, customVendor, colorOption, colorCode } = colorMatchGetValues();
			if (isCustomColor) {
				const colorMatchValid = await colorMatchTrigger();
				if (!colorMatchValid) {
					return;
				}
			}
			handleSetModificationFormErrors(errors);
			if (validity.every(Boolean) && isValid) {
				await handleSaveAction(true, requiredCategoriesGetValues());

				if (isCustomColor && configuration) {
					dispatch(
						newOrderActions.updateColorMatch({
							configurationId: configuration.configurationId,
							colorMatch: {
								vendorCode: paintVendor?.code,
								vendorDescription: customVendor ?? paintVendor?.description,
								colorCode: colorOption?.code ?? CustomText,
								colorDescription: colorCode ?? colorOption?.description
							}
						})
					);
					dispatch(
						newOrderActions.addDuplicateConfiguration({
							configuration: configuration,
							oldConfigId: configuration.configurationId,
							newConfigId: uuidv4()
						})
					);
				} else {
					inputRef?.current?.focus({ preventScroll: true });
					inputRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
				}
			}
		});
	};

	const formOnSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
		event.preventDefault();

		if (isCustomColor && configuration) {
			let productValid = true;
			let colorMatchValid = true;
			productValid = await trigger("productSelect");
			colorMatchValid = await colorMatchTrigger();
			if (!productValid || !colorMatchValid) {
				return;
			}
			const { paintVendor, customVendor, colorOption, colorCode } = colorMatchGetValues();

			dispatch(
				newOrderActions.updateColorMatch({
					configurationId: configuration.configurationId,
					colorMatch: {
						vendorCode: paintVendor?.code,
						vendorDescription: customVendor ?? paintVendor?.description,
						colorCode: colorOption?.code ?? CustomText,
						colorDescription: colorCode ?? colorOption?.description
					}
				})
			);
		}

		setSubmitted(new Date().getTime());
		const valuesToValidate = modificationFormValues;

		const { errors } = getModificationFormErrors(valuesToValidate);

		handleSetModificationFormErrors(errors);
		await requiredCategoriesHandleSubmit((requiredCategoryValues) => {
			handleSubmit(() => handleSaveAction(false, requiredCategoryValues))(event);
		})(event);
	};

	const containerRef = useRef<HTMLFormElement | null>(null);

	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, []);

	return (
		<form
			ref={containerRef}
			id="add-line-item-form"
			onSubmit={formOnSubmit}
			noValidate
			className={className}
		>
			<AddLineItemContainer>
				{!isCustomQuote && (
					<>
						<h6 data-testid="add-line-item-header">{AddLineItemHeader}</h6>
						<div data-testid="add-line-item-required">{AddLineItemRequired}</div>
					</>
				)}
				<AddLineItemInputFormControl>
					<Controller
						name="productSelect"
						control={control}
						render={({ field: { onChange }, fieldState: { error } }) => (
							<div>
								<AutoComplete
									dataTestId="add-line-item-product-search"
									options={sortedOptions ?? []}
									value={productSelect ?? ""}
									onChange={(event, values: models["ProductViewModel"]) => {
										handleChangeProduct(event, {
											sku: values.sku,
											description: values.description
										});
										onChange({ sku: values.sku, description: values.description });
									}}
									getOptionLabel={(value: models["ProductViewModel"]) =>
										convertSkuAndDescriptionToString(value.sku, value.description)
									}
									isLoading={isLoading}
									onMouseDownCapture={handleMouseDownCapture}
									isError={Boolean(error)}
									errorText={ProductSelectFormError}
									label={AddLineItemProductPlaceholder}
									virtualize
									required
									disableClearable
									autoFocus
									noOptionsText={AddLineItemInvalidProductError}
									inputRef={inputRef}
									filterOptions={filterOptions}
								/>
							</div>
						)}
					/>

					{!isCustomColor && !isCustomQuote && (
						<AutoComplete
							{...getFormFieldProps({
								name: "productSelectQuantity",
								...formFieldData
							})}
							options={quantity}
							value={quantityValue}
							onChange={handleQuantityChange}
							onMouseDownCapture={handleMouseDownCapture}
							getOptionLabel={(option) => String(option) || ""}
							defaultValue={quantityValue}
							isError={false}
							errorText=""
							label={AddLineItemQuantityPlaceholder}
							dataTestId="add-line-item-quantity"
							required
							disableClearable
						/>
					)}
				</AddLineItemInputFormControl>

				{((foundSearch && searchValue) || defaultItem) && !isCustomColor && (
					<>
						{foundSearch &&
							(isDimensionValid(foundSearch.standardDepth) ||
								isDimensionValid(foundSearch.standardHeight) ||
								isDimensionValid(foundSearch.standardWidth)) && (
								<AddLineItemStandardContainer>
									<div data-testid="add-line-item-standard-dimensions-header">
										{AddLineItemStandardDimensionsHeader}
									</div>
									<AddLineItemStandardDimensions isAddLineItem={isAddLineItem}>
										{isDimensionValid(foundSearch.standardDepth) && (
											<div data-testid="add-line-item-standard-dimensions-depth">
												<span>{AddLineDepthLabel}</span>
												<span>{foundSearch.standardDepth}&quot;</span>
											</div>
										)}

										{isDimensionValid(foundSearch.standardHeight) && (
											<div data-testid="add-line-item-standard-dimensions-height">
												<span>{AddLineHeightLabel}</span>
												<span>{foundSearch.standardHeight}&quot;</span>
											</div>
										)}

										{isDimensionValid(foundSearch.standardWidth) && (
											<div data-testid="add-line-item-standard-dimensions-width">
												<span>{AddLineWidthLabel}</span>
												<span>{foundSearch.standardWidth}&quot;</span>
											</div>
										)}
									</AddLineItemStandardDimensions>
								</AddLineItemStandardContainer>
							)}

						{((foundSearch?.requiredCategories && foundSearch.requiredCategories.length !== 0) ||
							(foundSearch?.requiredDimensions && foundSearch?.requiredDimensions?.length !== 0) ||
							(defaultPendingItem?.requiredDimensions &&
								defaultPendingItem.requiredDimensions.length !== 0)) && (
							<h6 data-testid="add-line-item-required-specifications-header">
								{SelectRequiredSpecifications}
							</h6>
						)}

						{((foundSearch?.requiredDimensions && foundSearch?.requiredDimensions.length !== 0) ||
							(defaultPendingItem?.requiredDimensions &&
								defaultPendingItem.requiredDimensions.length !== 0)) && (
							<DimensionContainer>
								{(foundSearch?.requiredDimensions ?? defaultPendingItem?.requiredDimensions)?.map(
									(dimension, dimensionIndex) => {
										return (
											dimension && (
												<div key={dimension.id}>
													{dimension.isUnrestrictedValue ? (
														<TextField
															key={dimension.id}
															type="number"
															label={`Enter ${dimension.description}`}
															value={
																requiredDimensionsValues[dimensionIndex]?.value ?? ""
															}
															onChange={(e: ChangeEvent<HTMLInputElement>) =>
																handleDimensionChange(
																	dimension.id,
																	e.target.value,
																	dimension.description,
																	dimension.increment,
																	dimension.maxValue,
																	dimension.minValue,
																	dimension.isUnrestrictedValue,
																	dimensionIndex
																)
															}
															required
															inputProps={{
																step: "any", // Allow any decimal value
																min: 0
															}}
															size="small"
															error={
																originalSubmitted.current !== submitted &&
																!requiredDimensionsValues[dimensionIndex]
															}
															helperText={
																originalSubmitted.current !== submitted &&
																!requiredDimensionsValues[dimensionIndex]
																	? RequiredDimensionError
																	: ""
															}
															fullWidth
															data-testid="add-line-item-dimensions-unrestricted"
														/>
													) : (
														<AutoComplete
															key={dimension.id}
															value={
																requiredDimensionsValues[dimensionIndex]?.value ?? null
															}
															options={calculateIntervals(
																dimension.minValue,
																dimension.maxValue,
																dimension.increment
															)}
															onMouseDownCapture={(e: SyntheticEvent) => {
																if (
																	e.target instanceof HTMLElement &&
																	e.target.nodeName === "INPUT"
																) {
																	e.stopPropagation();
																}
															}}
															onChange={(_, value) =>
																handleDimensionChange(
																	dimension.id,
																	value,
																	dimension.description,
																	dimension.increment,
																	dimension.maxValue,
																	dimension.minValue,
																	dimension.isUnrestrictedValue,
																	dimensionIndex
																)
															}
															isLoading={false}
															label={
																dimension.description
																	? "Select " + dimension.description
																	: ""
															}
															dataTestId="add-line-item-dimensions"
															getOptionLabel={(option) => String(option) + '"'}
															required
															isError={
																originalSubmitted.current !== submitted &&
																!requiredDimensionsValues[dimensionIndex]
															}
															errorText={RequiredDimensionError}
															disableClearable
														/>
													)}

													{!dimension.isUnrestrictedValue &&
														(submitted || dimensions.current[dimension.id ?? ""]) && (
															<RequiredDimensionsValues data-testid="add-line-item-dimension-mix-max">
																{`Min: ${dimension.minValue}", Max: ${dimension.maxValue}"`}
															</RequiredDimensionsValues>
														)}
												</div>
											)
										);
									}
								)}
							</DimensionContainer>
						)}
					</>
				)}

				{foundSearch?.requiredCategories && foundSearch.requiredCategories.length > 0 && !isCustomColor && (
					<RequiredCategoryWrapper>
						{foundSearch.requiredCategories.map((category) => (
							<Controller
								key={category.name}
								name={category.name ?? ""}
								control={requiredCategoriesControl}
								rules={{ required: true }}
								render={({ field: { onChange, value = null }, fieldState: { error } }) => (
									<AutoComplete
										onChange={(_e, value) => onChange(value)}
										value={value}
										options={
											category.values?.map((value) => ({
												...value,
												categoryName: category.name
											})) ?? []
										}
										isLoading={false}
										getOptionLabel={(category) => `${category?.sku} - ${category?.description}`}
										label={`Select ${category.name}`}
										dataTestId="add-line-item-required-category"
										required
										isError={Boolean(error)}
										errorText={RequiredDimensionError}
										disableClearable
									/>
								)}
							/>
						))}
					</RequiredCategoryWrapper>
				)}

				{foundSearch?.allowModifications && !isCustomColor && (
					<AddModification
						key={searchValue}
						handleClearFieldError={handleSetModificationFormErrors}
						onModificationChange={handleSetModificationFormData}
						selectedEditItem={defaultItemRef.current ? defaultItemRef : selectedEditItem}
						errors={modificationFormErrors}
						formValues={modificationFormValues}
						modifications={modifications}
						isCustomQuote={isCustomQuote}
					/>
				)}

				{isCustomColor && (
					<AddColorMatch
						colorMatchType={paintVendors?.type}
						vendors={paintVendors?.vendors ?? []}
						isLoadingVendors={isVendorsLoading}
						control={colorMatchControl}
						setValue={colorMatchSetValue}
						clearErrors={colorMatchClearErrors}
					/>
				)}

				{children && (
					<AddLineItemButtonWrapper isAddLineItem={isAddLineItem}>{children}</AddLineItemButtonWrapper>
				)}
				{!isCustomQuote && (
					<AddLineItemButtonWrapper isAddLineItem={isAddLineItem}>
						<div>
							<Button
								data-testid="add-line-item-cancel-button"
								variant="text"
								onClick={() => addItemVisibility?.()}
							>
								{AddLineCancelButton}
							</Button>
						</div>

						<div>
							<Button
								data-testid="add-line-item-save-button"
								variant="outlined"
								type="submit"
								data-id={`${isAddOnPage ? "add-ons" : "new-order"}-build-order-save-item-button`}
							>
								{AddLineSaveButton}
							</Button>
							<div>{AddLineEnterText}</div>
						</div>

						<div>
							<Button
								data-testid="add-line-item-add-another-button"
								variant="contained"
								onClick={handleAddAnother}
								data-id={`${isAddOnPage ? "add-ons" : "new-order"}-build-order-add-another-item-button`}
							>
								{isCustomColor ? AddAnotherColorText : AddLineAddAnotherItemButton}
							</Button>
							<div>{AddLineAddAnotherTextOne}</div>
						</div>
					</AddLineItemButtonWrapper>
				)}
			</AddLineItemContainer>
		</form>
	);
};

export default AddLineItem;
